import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldCheck, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [adInquiries, setAdInquiries] = useState([]);
  const [leads, setLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('leads');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      if (activeTab === 'leads') {
        const { data } = await axios.get(`${API}/leads`, { withCredentials: true });
        setLeads(data);
      } else if (activeTab === 'companies') {
        const { data } = await axios.get(`${API}/admin/pending-companies`, { withCredentials: true });
        setPendingCompanies(data);
      } else if (activeTab === 'users') {
        const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
        setUsers(data);
      } else if (activeTab === 'ads') {
        const { data } = await axios.get(`${API}/ad-inquiries`, { withCredentials: true });
        setAdInquiries(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const approveCompany = async (companyId) => {
    try {
      await axios.patch(`${API}/companies/${companyId}/approve`, {}, { withCredentials: true });
      toast.success('Company approved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve company');
    }
  };

  const deleteCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await axios.delete(`${API}/companies/${companyId}`, { withCredentials: true });
        toast.success('Company deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete company');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background_light">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck size={48} className="text-accent" weight="duotone" />
          <h1 className="text-4xl sm:text-5xl tracking-tight font-semibold">Admin Dashboard</h1>
        </div>

        <div className="flex gap-4 mb-8 border-b border-borders">
          <button
            onClick={() => setActiveTab('leads')}
            data-testid="tab-leads"
            className={`px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'leads'
                ? 'border-accent text-accent font-medium'
                : 'border-transparent text-gray-600 hover:text-primary'
            }`}
          >
            New Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('companies')}
            data-testid="tab-companies"
            className={`px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'companies'
                ? 'border-accent text-accent font-medium'
                : 'border-transparent text-gray-600 hover:text-primary'
            }`}
          >
            Pending Companies
          </button>
          <button
            onClick={() => setActiveTab('users')}
            data-testid="tab-users"
            className={`px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-accent text-accent font-medium'
                : 'border-transparent text-gray-600 hover:text-primary'
            }`}
          >
            All Users
          </button>
          <button
            onClick={() => setActiveTab('ads')}
            data-testid="tab-ads"
            className={`px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'ads'
                ? 'border-accent text-accent font-medium'
                : 'border-transparent text-gray-600 hover:text-primary'
            }`}
          >
            Ad Inquiries
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : (
          <>
            {activeTab === 'leads' && (
              <div className="bg-white border border-borders rounded-sm overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-borders">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Source</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borders">
                    {leads.map(lead => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium">{lead.company_name || 'N/A'}</td>
                        <td className="px-6 py-4">{lead.email}</td>
                        <td className="px-6 py-4">{lead.phone || 'N/A'}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-sm">
                            {lead.source}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-sm ${
                            lead.status === 'new' ? 'bg-green-100 text-green-800' :
                            lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'companies' && (
              <div className="space-y-6">
                {pendingCompanies.length === 0 ? (
                  <div className="bg-white border border-borders rounded-sm p-8 text-center">
                    <p className="text-gray-600">No pending companies</p>
                  </div>
                ) : (
                  pendingCompanies.map(company => (
                    <div key={company.id} className="bg-white border border-borders rounded-sm p-6" data-testid={`pending-company-${company.id}`}>
                      <h3 className="text-2xl tracking-tight font-medium mb-2">{company.name}</h3>
                      <p className="text-gray-600 mb-4">{company.location}</p>
                      {company.description && (
                        <p className="text-base leading-relaxed mb-4">{company.description}</p>
                      )}
                      <div className="flex gap-4">
                        <button
                          onClick={() => approveCompany(company.id)}
                          data-testid={`approve-${company.id}`}
                          className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-sm hover:bg-green-700 transition-colors"
                        >
                          <CheckCircle size={20} />
                          Approve
                        </button>
                        <button
                          onClick={() => deleteCompany(company.id)}
                          className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-sm hover:bg-red-700 transition-colors"
                        >
                          <XCircle size={20} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="bg-white border border-borders rounded-sm overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-borders">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-[0.2em]">Role</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borders">
                    {users.map(user => (
                      <tr key={user.id}>
                        <td className="px-6 py-4">{user.name}</td>
                        <td className="px-6 py-4">{user.email}</td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-gray-100 text-xs rounded-sm uppercase">{user.role}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'ads' && (
              <div className="space-y-6">
                {adInquiries.length === 0 ? (
                  <div className="bg-white border border-borders rounded-sm p-8 text-center">
                    <p className="text-gray-600">No ad inquiries yet</p>
                  </div>
                ) : (
                  adInquiries.map(inquiry => (
                    <div key={inquiry.id} className="bg-white border border-borders rounded-sm p-6">
                      <h3 className="text-2xl tracking-tight font-medium mb-2">{inquiry.company_name}</h3>
                      <p className="text-gray-600 mb-4">Contact: {inquiry.contact_name} ({inquiry.email})</p>
                      <p className="text-base leading-relaxed">{inquiry.message}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
